import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Typography, App, Checkbox } from 'antd';
import { UserOutlined, LockOutlined, MailOutlined, PhoneOutlined, ArrowRightOutlined, CheckCircleOutlined, SafetyCertificateOutlined, ShopOutlined, TeamOutlined, LineChartOutlined, FormOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { registerVendor } from '../../store/slices/authSlice';
import { VendorSignupForm } from '../../types';
import { AuthSplitLayout } from './AuthSplitLayout';
import { AuthFormCard } from './AuthFormCard';
import {
  AuthHeroBadge,
  AuthHeroTitle,
  AuthHeroDescription,
  AuthHeroHighlights,
  AuthHeroFeatureHeading,
  AuthHeroFeatureList,
} from './authHeroPrimitives';

const { Text } = Typography;

const inputClass =
  '!min-h-[2.75rem] rounded-lg border-slate-200 text-[15px] transition-colors hover:border-[#0171BF]/40 focus:border-[#0171BF]';

const Signup: React.FC = () => {
  const [form] = Form.useForm();
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { loading, error, isAuthenticated } = useAppSelector((state) => state.auth);
  const { message } = App.useApp();

  useEffect(() => {
    if (isAuthenticated) navigate('/dashboard');
  }, [isAuthenticated, navigate]);

  const onFinish = async (values: VendorSignupForm) => {
    if (!agreeToTerms) {
      message.error('Please agree to the Terms of Service and Privacy Policy');
      return;
    }
    try {
      await dispatch(registerVendor(values)).unwrap();
      message.success('Registration successful! Please check your email to verify your account.');
      navigate('/dashboard');
    } catch (err) {
      message.error(err as string);
    }
  };

  const highlights = [
    { value: 'One account', label: 'Vendor access' },
    { value: 'List & pitch', label: 'Products & services' },
    { value: 'Inbound', label: 'Leads from TOC' },
  ];

  const features = [
    {
      icon: <FormOutlined />,
      title: 'Quick registration',
      description: 'Create your login and contact details—finish store setup after you are in.',
    },
    {
      icon: <ShopOutlined />,
      title: 'Your catalogue',
      description: 'Add listings that reflect how you sell; we connect you with interested buyers.',
    },
    {
      icon: <TeamOutlined />,
      title: 'Enquiries, not checkout',
      description: 'Leads land with you—The Old Cart does not process orders on your behalf.',
    },
    {
      icon: <LineChartOutlined />,
      title: 'Grow visibility',
      description: 'Use the dashboard to refine what you offer and how you show up on the marketplace.',
    },
  ];

  const hero = (
    <>
      <AuthHeroBadge icon={<UserOutlined />}>Become a vendor</AuthHeroBadge>
      <AuthHeroTitle>Open your storefront on a lead-first marketplace</AuthHeroTitle>
      <AuthHeroDescription>
        Register once, then build your profile and publish products and services. Buyers discover you here; you convert
        conversations off-platform on your terms—same model as sign-in: listings and leads, not order fulfilment by TOC.
      </AuthHeroDescription>
      <AuthHeroHighlights items={highlights} />
      <AuthHeroFeatureHeading>After you join</AuthHeroFeatureHeading>
      <AuthHeroFeatureList items={features} />
    </>
  );

  const leftFooter = (
    <>
      <span className="inline-flex items-center gap-1">
        <CheckCircleOutlined className="text-[#4ADE80]" />
        Encrypted registration
      </span>
      <span className="text-white/50">·</span>
      <span className="inline-flex items-center gap-1 text-white/70">
        <SafetyCertificateOutlined className="text-xs text-[#F7941F]" />
        Same marketplace rules as sign-in
      </span>
    </>
  );

  return (
    <AuthSplitLayout hero={hero} footer={leftFooter}>
      <AuthFormCard
        maxWidthClass="w-full max-w-full sm:max-w-lg md:max-w-xl lg:max-w-[480px] xl:max-w-[520px]"
        title="Create vendor account"
        subtitle="A few details to get started—you can refine your business profile after your first sign-in."
      >
        <Form
          form={form}
          name="signup"
          onFinish={onFinish}
          layout="vertical"
          size="large"
          scrollToFirstError
          requiredMark={false}
          className="auth-form-fields-tight"
        >
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-x-3 sm:gap-y-0">
            <Form.Item
              name="firstName"
              rules={[
                { required: true, message: 'Enter your first name' },
                { min: 2, message: 'At least 2 characters' },
              ]}
            >
              <Input
                prefix={<UserOutlined className="text-[#0171BF]" />}
                placeholder="First name"
                autoComplete="given-name"
                aria-label="First name"
                className={inputClass}
              />
            </Form.Item>
            <Form.Item
              name="lastName"
              rules={[
                { required: true, message: 'Enter your last name' },
                { min: 2, message: 'At least 2 characters' },
              ]}
            >
              <Input
                prefix={<UserOutlined className="text-[#0171BF]" />}
                placeholder="Last name"
                autoComplete="family-name"
                aria-label="Last name"
                className={inputClass}
              />
            </Form.Item>
          </div>

          <Form.Item
            name="email"
            rules={[
              { required: true, message: 'Enter your email' },
              { type: 'email', message: 'Invalid email' },
            ]}
          >
            <Input
              prefix={<MailOutlined className="text-[#0171BF]" />}
              placeholder="Email address"
              autoComplete="email"
              aria-label="Email address"
              className={inputClass}
            />
          </Form.Item>

          <Form.Item
            name="phone"
            rules={[
              { required: true, message: 'Enter your phone number' },
              { pattern: /^[6-9]\d{9}$/, message: 'Valid 10-digit Indian mobile' },
            ]}
          >
            <Input
              prefix={<PhoneOutlined className="text-[#0171BF]" />}
              placeholder="Phone number (10 digits)"
              maxLength={10}
              autoComplete="tel"
              aria-label="Phone number"
              className={inputClass}
            />
          </Form.Item>

          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-x-3 sm:gap-y-0">
            <Form.Item
              name="password"
              rules={[
                { required: true, message: 'Enter a password' },
                { min: 8, message: 'At least 8 characters' },
                {
                  pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
                  message: 'Upper, lower, number & special char',
                },
              ]}
            >
              <Input.Password
                prefix={<LockOutlined className="text-[#0171BF]" />}
                placeholder="Password (8+ chars, Aa1!)"
                autoComplete="new-password"
                aria-label="Password"
                className={inputClass}
              />
            </Form.Item>
            <Form.Item
              name="confirmPassword"
              dependencies={['password']}
              rules={[
                { required: true, message: 'Confirm your password' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) return Promise.resolve();
                    return Promise.reject(new Error('Passwords do not match'));
                  },
                }),
              ]}
            >
              <Input.Password
                prefix={<LockOutlined className="text-[#0171BF]" />}
                placeholder="Confirm password"
                autoComplete="new-password"
                aria-label="Confirm password"
                className={inputClass}
              />
            </Form.Item>
          </div>

          <div className="mb-3 rounded-lg border border-slate-100 bg-slate-50/80 px-3 py-2.5 sm:px-3.5">
              <Form.Item name="agreeToTerms" valuePropName="checked" className="auth-form-item--compact">
              <Checkbox checked={agreeToTerms} onChange={(e) => setAgreeToTerms(e.target.checked)} className="[&_.ant-checkbox-inner]:rounded-lg">
                <span className="text-sm text-slate-600">
                  I agree to the{' '}
                  <Link to="/terms" className="font-medium text-[#0171BF] hover:underline">
                    Terms of Service
                  </Link>{' '}
                  and{' '}
                  <Link to="/privacy" className="font-medium text-[#0171BF] hover:underline">
                    Privacy Policy
                  </Link>
                </span>
              </Checkbox>
            </Form.Item>
          </div>

          {error && (
            <div className="mb-3 rounded-lg border border-red-200/80 bg-red-50/90 px-3.5 py-2.5" role="alert">
              <Text className="text-sm text-red-700">{error}</Text>
            </div>
          )}

          <Form.Item className="auth-form-item--compact">
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              block
              size="large"
              icon={<ArrowRightOutlined />}
              iconPosition="end"
              className="!h-12 !rounded-lg !text-[15px] !font-semibold shadow-md"
            >
              Create vendor account
            </Button>
          </Form.Item>
        </Form>

        <p className="mt-7 text-center text-sm text-slate-600 sm:mt-8">
          <span className="whitespace-normal sm:whitespace-nowrap">
            Already have an account?{' '}
            <Link to="/login" className="font-semibold text-[#0171BF] hover:underline">
              Sign in
            </Link>
          </span>
        </p>

        <p className="mt-6 text-center text-xs leading-relaxed text-slate-500 sm:text-[13px]">
          By registering you confirm you will use The Old Cart only for legitimate vendor listings and that you
          understand we facilitate leads—we do not run order processing for your business.
        </p>
      </AuthFormCard>
    </AuthSplitLayout>
  );
};

export default Signup;
